import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Check, Search, Layers } from 'lucide-react';
import { projectService } from '../../services/api';
import type { Project } from '../../types';

interface ProjectSelectorHeaderProps {
    currentProject: Project | null;
    onSelectProject: (project: Project) => void;
    theme: 'light' | 'dark' | 'gradient';
}

// Generate consistent color from project name
const getProjectColor = (name?: string): string => {
    const colors = [
        'from-rose-500 to-pink-600',
        'from-violet-500 to-purple-600',
        'from-blue-500 to-indigo-600',
        'from-cyan-500 to-teal-600',
        'from-emerald-500 to-green-600',
        'from-amber-500 to-orange-600',
        'from-red-500 to-rose-600',
        'from-fuchsia-500 to-pink-600',
    ];
    if (!name) return colors[0];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
};

export const ProjectSelectorHeader: React.FC<ProjectSelectorHeaderProps> = ({
    currentProject,
    onSelectProject,
    theme
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [projects, setProjects] = useState<Project[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Calculate dropdown position based on available space
    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = Math.min(400, projects.length * 72 + 120);

        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setDropdownPosition('top');
        } else {
            setDropdownPosition('bottom');
        }
    }, [projects.length]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadProjects();
            calculatePosition();
            // Focus search input after opening
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [isOpen, calculatePosition]);

    useEffect(() => {
        window.addEventListener('resize', calculatePosition);
        return () => window.removeEventListener('resize', calculatePosition);
    }, [calculatePosition]);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredProjects(projects);
        } else {
            const query = searchQuery.toLowerCase();
            setFilteredProjects(
                projects.filter(p =>
                    p.name.toLowerCase().includes(query) ||
                    p.description?.toLowerCase().includes(query)
                )
            );
        }
    }, [searchQuery, projects]);

    const loadProjects = async () => {
        setIsLoading(true);
        try {
            const data = await projectService.getProjects();
            setProjects(data);
            setFilteredProjects(data);
        } catch (error) {
            // Error loading projects
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = (project: Project) => {
        onSelectProject(project);
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearchQuery('');
        }
    };

    // Theme styles
    const isDark = theme !== 'light';

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                ref={triggerRef}
                onClick={handleToggle}
                className={`
                    group flex items-center gap-3 sm:gap-4
                    px-3 sm:px-4 py-2 sm:py-3 -ml-3 sm:-ml-4
                    rounded-2xl
                    transition-all duration-300 ease-out
                    ${isDark
                        ? 'hover:bg-white/10 active:bg-white/15'
                        : 'hover:bg-black/5 active:bg-black/10'
                    }
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50
                `}
            >
                {/* Project Avatar with gradient */}
                <div className={`
                    relative w-10 h-10 sm:w-12 sm:h-12 rounded-2xl
                    flex items-center justify-center
                    text-base sm:text-lg font-bold text-white
                    bg-gradient-to-br ${currentProject ? getProjectColor(currentProject.name) : 'from-gray-400 to-gray-500'}
                    shadow-lg
                    transition-all duration-300 ease-out
                    group-hover:scale-105 group-hover:shadow-xl
                    group-hover:rotate-[-2deg]
                `}>
                    {currentProject?.name?.charAt(0).toUpperCase() || '?'}
                    {/* Subtle shine effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/25 to-transparent" />
                </div>

                {/* Text Content */}
                <div className="flex flex-col items-start min-w-0">
                    <span className={`
                        text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider
                        ${isDark ? 'text-white/50' : 'text-gray-400'}
                    `}>
                        Proyecto
                    </span>
                    <span className={`
                        text-sm sm:text-base font-semibold tracking-tight
                        ${isDark ? 'text-white' : 'text-gray-900'}
                        max-w-[120px] sm:max-w-[200px] truncate
                    `}>
                        {currentProject?.name || 'Seleccionar'}
                    </span>
                </div>

                {/* Chevron with rotation */}
                <div className={`
                    w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center
                    ${isDark ? 'bg-white/10' : 'bg-black/5'}
                    transition-all duration-300 ease-out
                    group-hover:${isDark ? 'bg-white/20' : 'bg-black/10'}
                `}>
                    <ChevronDown className={`
                        w-4 h-4
                        ${isDark ? 'text-white/70' : 'text-gray-500'}
                        transition-transform duration-300 ease-out
                        ${isOpen ? 'rotate-180' : 'rotate-0'}
                    `} />
                </div>
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <>
                    {/* Mobile: Full screen overlay */}
                    <div className="fixed inset-0 bg-black/50 z-[100] sm:hidden" onClick={() => setIsOpen(false)} />

                    <div
                        className={`
                            fixed sm:absolute z-[101]

                            /* Mobile: Bottom sheet */
                            inset-x-0 bottom-0 sm:inset-auto

                            /* Desktop: Dropdown */
                            sm:${dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
                            sm:left-0 sm:w-80

                            /* Shared styles */
                            ${isDark
                                ? 'bg-gray-900/98 border-white/10'
                                : 'bg-white/98 border-gray-200'
                            }
                            backdrop-blur-xl
                            border-t sm:border sm:rounded-2xl
                            shadow-2xl

                            /* Mobile rounded top */
                            rounded-t-3xl sm:rounded-2xl

                            /* Animation */
                            animate-in
                            sm:fade-in-0 sm:zoom-in-95
                            slide-in-from-bottom sm:slide-in-from-bottom-0
                            duration-200
                        `}
                        style={{ maxHeight: 'calc(100vh - 100px)' }}
                    >
                        {/* Header with drag indicator (mobile) */}
                        <div className="sm:hidden flex justify-center pt-3 pb-1">
                            <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
                        </div>

                        {/* Search Bar */}
                        <div className="px-4 pt-3 pb-2">
                            <div className={`
                                relative flex items-center gap-2
                                px-4 py-3 rounded-xl
                                ${isDark ? 'bg-white/5' : 'bg-gray-100'}
                                transition-all duration-200
                                focus-within:ring-2 focus-within:ring-blue-500/30
                            `}>
                                <Search className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Buscar proyecto..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`
                                        flex-1 bg-transparent border-none outline-none
                                        text-sm placeholder:${isDark ? 'text-white/30' : 'text-gray-400'}
                                        ${isDark ? 'text-white' : 'text-gray-900'}
                                    `}
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className={`text-xs ${isDark ? 'text-white/40 hover:text-white/60' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Projects List */}
                        <div className="overflow-y-auto overscroll-contain px-2 pb-4" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <div className={`
                                        w-8 h-8 rounded-full border-2 border-t-transparent
                                        ${isDark ? 'border-blue-400/50' : 'border-blue-500/50'}
                                        animate-spin
                                    `} />
                                    <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                                        Cargando proyectos...
                                    </span>
                                </div>
                            ) : filteredProjects.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 gap-3">
                                    <Layers className={`w-10 h-10 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                                    <span className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                                        {searchQuery ? 'Sin resultados' : 'No hay proyectos'}
                                    </span>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {filteredProjects.map((project, index) => {
                                        const isSelected = project.id === currentProject?.id;
                                        const colorClass = getProjectColor(project.name);

                                        return (
                                            <button
                                                key={project.id}
                                                onClick={() => handleSelect(project)}
                                                className={`
                                                    w-full text-left p-3 sm:p-3
                                                    flex items-center gap-3 sm:gap-4
                                                    rounded-xl sm:rounded-xl
                                                    transition-all duration-200 ease-out
                                                    ${isSelected
                                                        ? (isDark ? 'bg-blue-500/20' : 'bg-blue-50')
                                                        : (isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50')
                                                    }
                                                    group/item
                                                `}
                                                style={{
                                                    animationDelay: `${index * 30}ms`
                                                }}
                                            >
                                                {/* Project Avatar */}
                                                <div className={`
                                                    relative w-11 h-11 sm:w-12 sm:h-12 rounded-xl
                                                    flex items-center justify-center flex-shrink-0
                                                    text-sm sm:text-base font-bold text-white
                                                    bg-gradient-to-br ${colorClass}
                                                    shadow-md
                                                    transition-all duration-200
                                                    group-hover/item:scale-105 group-hover/item:shadow-lg
                                                `}>
                                                    {project.name?.charAt(0).toUpperCase()}
                                                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent" />
                                                </div>

                                                {/* Project Info */}
                                                <div className="flex-1 min-w-0 py-0.5">
                                                    <div className={`
                                                        text-sm sm:text-[15px] font-semibold tracking-tight
                                                        ${isSelected
                                                            ? (isDark ? 'text-blue-400' : 'text-blue-600')
                                                            : (isDark ? 'text-white' : 'text-gray-900')
                                                        }
                                                        truncate
                                                    `}>
                                                        {project.name}
                                                    </div>
                                                    {project.description && (
                                                        <div className={`
                                                            text-xs sm:text-[13px] mt-0.5
                                                            ${isDark ? 'text-white/40' : 'text-gray-500'}
                                                            truncate
                                                        `}>
                                                            {project.description}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Selection Indicator */}
                                                {isSelected && (
                                                    <div className={`
                                                        w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                                                        ${isDark ? 'bg-blue-500' : 'bg-blue-500'}
                                                    `}>
                                                        <Check className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className={`
                            px-4 py-3 border-t
                            ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-gray-100 bg-gray-50/50'}
                        `}>
                            <div className="flex items-center justify-between">
                                <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                    {filteredProjects.length} proyecto{filteredProjects.length !== 1 ? 's' : ''}
                                </span>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className={`
                                        text-xs font-medium px-3 py-1.5 rounded-lg
                                        ${isDark
                                            ? 'text-white/60 hover:text-white hover:bg-white/10'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                        }
                                        transition-colors duration-200
                                        sm:hidden
                                    `}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
